/**
 * «Профіль споживання»: the profile the internal arbitrage plan uses — the
 * median by hour over 30 days (D2: of the consumption power), or the client's
 * own values — with the spread of days 10–90 %; «Скоригувати вручну» edits it
 * on the spot, «Повернути автоматичний» drops the manual values.
 */
import { useEffect, useState } from 'react';
import type { ExpectedLoadResponse } from 'shared';

import { ProfileChart } from '../../../components/charts/Charts.js';
import { errorText } from '../../../components/errors.js';
import { addDays, fmtDate, fmtNum, parseNum } from '../../../components/format.js';
import { useSaveExpectedLoad } from '../../../hooks/use-expected-load.js';
import { useLocale } from '../../../i18n/locale-context.js';

const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

export function ProfileCard({
  siteId,
  profile,
  today,
}: {
  siteId: string;
  profile: ExpectedLoadResponse | null | undefined;
  today: string;
}) {
  const { t, tf, locale } = useLocale();
  const fmt = (v: number, d = 1) => fmtNum(locale, v, d);
  const save = useSaveExpectedLoad(siteId);
  const [open, setOpen] = useState(false);
  const values = profile?.hourly_kwh.length === 24 ? profile.hourly_kwh : null;
  const [texts, setTexts] = useState<string[]>(() => HOURS.map(() => ''));
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setTexts(HOURS.map((_, h) => (values ? fmtNum(locale, values[h] ?? 0, 2) : '')));
  }, [values, locale]);

  const total = values?.reduce((a, v) => a + v, 0) ?? 0;
  const days = profile?.coverage_days ?? 30;
  const note = !values
    ? t('site.profile.none')
    : profile?.source === 'manual'
      ? tf('site.profile.note.manual', { total: fmt(total) })
      : tf('site.profile.note.auto', {
          days,
          from: fmtDate(addDays(today, -days)),
          to: fmtDate(addDays(today, -1)),
          total: fmt(total),
        });
  const spread = profile?.spread_kwh ?? [];

  const onSave = () => {
    const parsed = texts.map(parseNum);
    if (parsed.some((v) => !Number.isFinite(v) || v < 0)) {
      setMsg(t('site.profile.err'));
      return;
    }
    save.mutate(parsed, {
      onSuccess: () => setMsg(t('site.profile.saved')),
      onError: (err) => setMsg(errorText(err, tf)),
    });
  };
  const onReset = () =>
    save.mutate(null, {
      onSuccess: () => setMsg(t('site.profile.reset_done')),
      onError: (err) => setMsg(errorText(err, tf)),
    });

  return (
    <section className="card" aria-labelledby="prof-title">
      <div className="card-head">
        <h2 id="prof-title">{t('site.profile.title')}</h2>
        <button
          type="button"
          className="btn"
          aria-expanded={open}
          aria-controls="prof-edit"
          onClick={() => setOpen(!open)}
        >
          {t('site.profile.edit')}
        </button>
      </div>
      <div className="pricenote">{note}</div>
      {values ? (
        <>
          <div className="legend">
            <span>
              <i className="sw" style={{ background: '#04285d' }} />
              {t(profile?.source === 'manual' ? 'site.profile.legend.manual' : 'site.profile.legend.median')}
            </span>
            {spread.some(Boolean) ? (
              <span>
                <i className="sw line" style={{ background: '#8294ae' }} />
                {t('site.profile.legend.spread')}
              </span>
            ) : null}
          </div>
          <ProfileChart
            values={values}
            spread={spread}
            labels={HOURS}
            fmt={fmt}
            title={t('site.profile.title')}
            lines={(h) => {
              const s = spread[h];
              return [
                <b key="h">
                  {HOURS[h]}–{HOURS[h + 1] ?? '24:00'}
                </b>,
                tf('site.tip.median', { v: fmt(values[h] ?? 0, 2) }),
                ...(s ? [tf('site.tip.spread', { lo: fmt(s[0], 2), hi: fmt(s[1], 2) })] : []),
              ];
            }}
          />
        </>
      ) : null}
      {open ? (
        <div className="profile-edit" id="prof-edit">
          <div className="sub" style={{ marginBottom: 10 }}>
            {t('site.profile.edit.sub')}
          </div>
          <div className="profile-grid">
            {HOURS.map((label, h) => (
              <label key={label}>
                {label}
                <input
                  inputMode="decimal"
                  value={texts[h] ?? ''}
                  onChange={(e) => setTexts(texts.map((v, k) => (k === h ? e.target.value : v)))}
                />
              </label>
            ))}
          </div>
          <div className="row-actions">
            <button type="button" className="btn primary" onClick={onSave} disabled={save.isPending}>
              {t('site.profile.save')}
            </button>
            <button
              type="button"
              className="btn"
              onClick={onReset}
              disabled={save.isPending || profile?.source !== 'manual'}
            >
              {t('site.profile.reset')}
            </button>
            {msg ? (
              <span className="sub" aria-live="polite">
                {msg}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
